/**
 * What is behind one figure on the demo report.
 *
 * Eight tiles, each a number and nothing else. "29 no-shows" is a fact
 * nobody can act on until they know which twenty-nine, and the only way
 * to find out was to leave the report, go to the bookings screen and
 * rebuild the same cut by hand with the filters.
 *
 * Opening a tile lists exactly the rows it counted.
 *
 * ## Two shapes, because the tiles count two things
 *
 * Four of them count demos — scheduled, completed, no-show, cancelled —
 * and list one row per booking. The other four count people, and list one
 * row per person with their sessions under it. A person with four demos
 * is four demos and one person, and a list that blurs the two is a list
 * nobody can reconcile against the tile that opened it.
 *
 * That is also why 56 registered and 78 not registered come to 134 and
 * not 162: 162 demos were held with 134 people, 27 of whom came more than
 * once. The sheet says which of the two it is counting, at the top.
 */
import { useMemo, useState } from 'react';
import Sheet from '../Sheet.jsx';
import Icon from '../../lib/icons.jsx';
import { Avatar, Pill, EmptyState } from '../Primitives.jsx';
import { stamp, stateOf, STATE_META, REGISTERED, REG_META } from '../../domain/demos.js';

/*
 * What each tile counted, said once.
 *
 * `of` is which list to cut — the bookings or the people — and `pick` is
 * the cut itself, written the same way the tile's own count is so the two
 * cannot drift apart.
 */
export const TILE_DETAIL = {
  scheduled: {
    of: 'demos', title: 'Demos scheduled', icon: 'cal', tone: 'g-blue',
    note: 'Every demo booked into this period, whatever became of it.',
    pick: () => true
  },
  completed: {
    of: 'demos', title: 'Completed', icon: 'check', tone: 'g-green',
    note: 'The demo happened — Calendly did not mark a no-show, or somebody said so here.',
    pick: (b, now) => stateOf(b, now) === 'done'
  },
  noshow: {
    of: 'demos', title: 'No-shows', icon: 'eyeoff', tone: 'g-amber',
    note: 'Marked as a no-show by whoever ran the meeting — on Calendly, or here for one added by hand.',
    pick: (b, now) => stateOf(b, now) === 'noshow'
  },
  cancelled: {
    of: 'demos', title: 'Cancelled', icon: 'x', tone: 'g-red',
    note: 'Called off before the day, by the invitee or by us.',
    pick: (b, now) => stateOf(b, now) === 'canceled'
  },
  upcoming: {
    of: 'demos', title: 'Still to come', icon: 'clock', tone: 'g-indigo',
    note: 'Booked into this period, with the slot still ahead of us.',
    pick: (b, now) => ['upcoming', 'live'].includes(stateOf(b, now))
  },
  registered: {
    of: 'people', title: 'Registered users', icon: 'users', tone: 'g-cyan',
    note: 'People who already had an account — matched to one, or marked registered by hand.',
    pick: p => p.how !== 'none'
  },
  unregistered: {
    of: 'people', title: 'Not registered', icon: 'search', tone: 'g-slate',
    note: 'People with no account. Nothing matched their address, their number or their business name.',
    pick: p => p.how === 'none'
  },
  repeat: {
    of: 'people', title: 'Came more than once', icon: 'refresh', tone: 'g-violet',
    note: 'People with more than one demo in this period. Open a row to see the sessions.',
    pick: p => p.sessions > 1
  },
  follow: {
    of: 'people', title: 'Under follow-up', icon: 'flag', tone: 'g-orange',
    note: 'Handed to sales and not closed — either waiting to be picked up or being chased.',
    pick: p => p.chasing
  },
  b2b: {
    of: 'people', title: 'B2B partners', icon: 'building', tone: 'g-purple',
    note: 'Partners running sessions with us. The tile counts their sessions; this counts them.',
    pick: p => p.partner
  }
};

/** One demo, as a row. */
function DemoLine({ b, now }) {
  const st = stateOf(b, now);
  const meta = STATE_META[st] || [];
  const s = b.session || {};
  return (
    <li className={`tdrow ${st}`}>
      <Avatar name={b.invitee.name || b.invitee.email || '?'} />
      <div className="tdwho">
        <b>{b.invitee.name || b.invitee.email || 'Unknown'}</b>
        <small>{b.invitee.email || '—'}{s.business ? ` · ${s.business}` : ''}</small>
      </div>
      <div className="tdwhen">
        <b>{stamp(b.start)}</b>
        <small>{b.eventTypeName || b.name}</small>
      </div>
      <div className="tdtags">
        {meta[0] ? <Pill tone={meta[1]} icon={meta[2]}>{meta[0]}</Pill> : null}
        {/* Which of this person's demos this is, counted over everything
            we have rather than over the period on screen: "their third"
            is a fact about them, not about September. */}
        {s.demoCount > 1
          ? <Pill tone="gr-violet" title="This person has had more than one demo">
            Demo {s.demoIndex} of {s.demoCount}
          </Pill>
          : null}
        {s.customerType === 'B2B' ? <Pill tone="gr-indigo">B2B</Pill> : null}
      </div>
    </li>
  );
}

/** One person, with their sessions underneath. */
function PersonLine({ p, open, onToggle }) {
  const reg = REG_META[p.how] || REG_META.none;
  return (
    <>
      <li className={`tdrow person${open ? ' on' : ''}`} onClick={onToggle}>
        <Avatar name={p.name} />
        <div className="tdwho">
          <b>
            {p.name}
            {p.code ? <span className="tdcode">{p.code}</span> : null}
          </b>
          <small>{p.email || '—'}{p.business ? ` · ${p.business}` : ''}</small>
        </div>
        <div className="tdwhen">
          <b>{p.sessions} demo{p.sessions === 1 ? '' : 's'}</b>
          <small>
            {p.completed ? `${p.completed} held` : 'none held'}
            {p.noShow ? ` · ${p.noShow} no-show` : ''}
          </small>
        </div>
        <div className="tdtags">
          <Pill tone={reg[1]}>{reg[0]}</Pill>
          {p.partner ? <Pill tone="gr-indigo">B2B</Pill> : null}
          {p.chasing ? <Pill tone="gr-orange" icon="flag">Chasing</Pill> : null}
          {/* Only where there is more than one session to open, so the
              chevron never promises something that is not there. */}
          {p.demos.length > 1 ? <Icon name="chev" className={`tdchev${open ? ' on' : ''}`} /> : null}
        </div>
      </li>
      {open && p.demos.length > 1 ? (
        <li className="tdsessions">
          <ol>
            {p.demos.map(d => (
              <li key={d.id}>
                <b>{stamp(d.start)}</b>
                <span>{d.meeting}</span>
                <em>{(STATE_META[d.state] || [])[0] || d.state}</em>
                {d.owner ? <span className="dim">{d.owner}</span> : null}
              </li>
            ))}
          </ol>
        </li>
      ) : null}
    </>
  );
}

/*
 * One side of the register, with a tab per outcome.
 *
 * Opened either from the panel itself - "show me the 76" - or from one
 * of its rows, which opens the same sheet with that tab already chosen.
 * One sheet rather than eight, because somebody looking at the
 * completed demos almost always wants the no-shows next, and closing a
 * sheet to open another one to compare them is not reading, it is
 * fetching.
 */
const SIDE_TABS = [
  { k: 'all', label: 'All', states: null },
  { k: 'done', label: 'Completed', states: ['done'] },
  { k: 'noshow', label: 'No-show', states: ['noshow'] },
  { k: 'canceled', label: 'Cancelled', states: ['canceled'] },
  { k: 'upcoming', label: 'Still to come', states: ['upcoming', 'live'] }
];

/*
 * "reg", or "done:reg" - the side on its own, or a tab and the side.
 * Both land on the same sheet; the second just arrives with a tab
 * already chosen.
 */
function sideSpec(key) {
  const parts = String(key).split(':');
  const side = parts.length === 2 ? parts[1] : parts[0];
  const tab = parts.length === 2 ? parts[0] : 'all';
  if (!['reg', 'not'].includes(side)) return null;
  if (!SIDE_TABS.some(t => t.k === tab)) return null;

  const registered = side === 'reg';
  return {
    side,
    tab,
    registered,
    icon: registered ? 'users' : 'search',
    tone: registered ? 'g-cyan' : 'g-slate',
    title: registered ? 'Registered' : 'Not registered',
    note: registered
      ? 'Demos with people who already had an account on our books.'
      : 'Demos with people we had nothing on — not their address, their number, nor their business name.',
    /* REGISTERED is the rule the panels count on, so a tab can never
       disagree with the figure that opened it. */
    belongs: b => (REGISTERED(b) !== 'none') === registered
  };
}

/** The rows, filtered by whatever was typed. */
function findIn(list, q, of) {
  const needle = q.trim().toLowerCase();
  if (!needle) return list;
  return list.filter(x => {
    const hay = of === 'demos'
      ? `${x.invitee?.name} ${x.invitee?.email} ${x.session?.business} ${x.eventTypeName}`
      : `${x.name} ${x.email} ${x.business} ${x.code}`;
    return hay.toLowerCase().includes(needle);
  });
}

/** A box to type into, offered once a list is too long to scan. */
function Find({ q, setQ, unit }) {
  return (
    <div className="tdfind">
      <Icon name="search" />
      <input
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder={`Find a ${unit}…`}
        aria-label={`Find a ${unit}`}
      />
      {q ? <button type="button" className="iconbtn" onClick={() => setQ('')} aria-label="Clear"><Icon name="x" /></button> : null}
    </div>
  );
}

/**
 * One side of the register, a tab per outcome.
 *
 * The tab counts are of the whole side, not of what is typed in the
 * search box - a tab that changed its own number as you typed would
 * stop being a way to move between groups and become another filter.
 */
function SideSheet({ spec, cur, period, now, onClose }) {
  const [tab, setTab] = useState(spec.tab);
  const [q, setQ] = useState('');

  const mine = useMemo(
    () => (cur.rows || []).filter(spec.belongs),
    [cur, spec]
  );

  const counts = useMemo(() => {
    const out = {};
    SIDE_TABS.forEach(t => {
      out[t.k] = t.states
        ? mine.filter(b => t.states.includes(stateOf(b, now))).length
        : mine.length;
    });
    return out;
  }, [mine, now]);

  const here = SIDE_TABS.find(t => t.k === tab) || SIDE_TABS[0];
  const rows = useMemo(() => {
    const list = here.states ? mine.filter(b => here.states.includes(stateOf(b, now))) : mine;
    return findIn(list, q, 'demos');
  }, [mine, here, now, q]);

  return (
    <Sheet
      icon={spec.icon}
      tone={spec.tone}
      title={spec.title}
      onClose={onClose}
      closeLabel="Close"
      wide
      bodyClass="tdbody"
      hero={(
        <div className={`tdhero ${spec.registered ? 'reg' : 'unreg'}`}>
          <div>
            <b>{mine.length}</b>
            <span>{mine.length === 1 ? 'demo' : 'demos'}</span>
          </div>
          <p>
            {spec.note} <i>{period.label}.</i>
            {' '}Counted per demo — somebody with two demos is in it twice.
          </p>
        </div>
      )}
    >
      {/* A tab per outcome, each carrying its own count, so the choice is
          made by looking rather than by opening one to find out. */}
      <div className="tdtabs" role="tablist">
        {SIDE_TABS.filter(t => counts[t.k] || t.k === 'all').map(t => (
          <button
            key={t.k}
            type="button"
            role="tab"
            aria-selected={t.k === tab}
            className={`tdtab${t.k === tab ? ' on' : ''}`}
            onClick={() => { setTab(t.k); setQ(''); }}
          >
            {t.label}
            <em>{counts[t.k]}</em>
          </button>
        ))}
      </div>

      {(here.states ? counts[here.k] : mine.length) > 12
        ? <Find q={q} setQ={setQ} unit="demo" /> : null}

      {rows.length ? (
        <ul className="tdlist">
          {rows.map(b => <DemoLine key={b.id} b={b} now={now} />)}
        </ul>
      ) : (
        <EmptyState
          icon={spec.icon}
          tone={`gr-${spec.tone.replace('g-', '')}`}
          title={q ? 'Nothing matches' : 'None of these'}
        >
          {q
            ? 'Try a different name or address.'
            : `No ${here.label.toLowerCase()} demos on this side in ${period.label}.`}
        </EmptyState>
      )}
    </Sheet>
  );
}

export default function TileDetail({ tile, cur, period, now, onClose }) {
  /* Two shapes: a tile opens one list, a side of the register opens a
     tabbed one. Which is decided by the key that was clicked. */
  const side = sideSpec(tile);
  const spec = TILE_DETAIL[tile];
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState('');

  const rows = useMemo(() => {
    if (!spec) return [];
    const list = spec.of === 'demos'
      ? (cur.rows || []).filter(b => spec.pick(b, now))
      : (cur.peopleList || []).filter(p => spec.pick(p));
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(x => {
      const hay = spec.of === 'demos'
        ? `${x.invitee?.name} ${x.invitee?.email} ${x.session?.business} ${x.eventTypeName}`
        : `${x.name} ${x.email} ${x.business} ${x.code}`;
      return hay.toLowerCase().includes(needle);
    });
  }, [spec, cur, now, q]);

  if (side) return <SideSheet spec={side} cur={cur} period={period} now={now} onClose={onClose} />;
  if (!spec) return null;
  const unit = spec.of === 'demos' ? 'demo' : 'person';
  const units = spec.of === 'demos' ? 'demos' : 'people';

  return (
    <Sheet
      icon={spec.icon}
      tone={spec.tone}
      title={spec.title}
      onClose={onClose}
      closeLabel="Close"
      wide
      bodyClass="tdbody"
      hero={(
        <div className="tdhero">
          <div>
            <b>{rows.length}</b>
            <span>{rows.length === 1 ? unit : units}</span>
          </div>
          <p>
            {spec.note}
            {' '}
            <i>{period.label}.</i>
            {/* Said plainly, because the two kinds of tile do not add up to
                each other and that is the first thing anybody notices. */}
            {spec.of === 'people' ? ' Counted per person — somebody with three demos is one row.' : null}
          </p>
        </div>
      )}
    >
      {(spec.of === 'demos' ? (cur.rows || []) : (cur.peopleList || [])).length > 12 ? (
        <div className="tdfind">
          <Icon name="search" />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={`Find a ${unit}…`}
            aria-label={`Find a ${unit}`}
          />
          {q ? <button type="button" className="iconbtn" onClick={() => setQ('')} aria-label="Clear"><Icon name="x" /></button> : null}
        </div>
      ) : null}

      {rows.length ? (
        <ul className="tdlist">
          {spec.of === 'demos'
            ? rows.map(b => <DemoLine key={b.id} b={b} now={now} />)
            : rows.map(p => (
              <PersonLine
                key={p.key}
                p={p}
                open={open === p.key}
                onToggle={() => setOpen(open === p.key ? null : p.key)}
              />
            ))}
        </ul>
      ) : (
        <EmptyState icon={spec.icon} tone={`gr-${spec.tone.replace('g-', '')}`} title={q ? 'Nothing matches' : `No ${units} here`}>
          {q ? 'Try a different name or address.' : `Nothing in ${period.label} fell into this group.`}
        </EmptyState>
      )}
    </Sheet>
  );
}
